import { useCallback, useEffect, useState } from 'react';
import styled from 'styled-components';

import { BaseWorkTaskTypeEnum, WorkTaskDTO2, WorkTaskStatus } from '../../../../api/api';
import { WarningIcon } from '../../../../assets/icons/WarningIcon';
import Button from '../../../../components/button/Button';
import Link from '../../../../components/link/Link';
import StatusTag from '../../../../components/status-tag/StatusTag';
import Typography from '../../../../components/typography/Typography';
import NotificationService from '../../../../services/NotificationService';
import TaskService from '../../../../services/TaskService';
import theme from '../../../../theme/light-theme';
import {
  CONTROL_ROOM_DEPARTMENT_ID,
  PLANNING_DEPARTMENT_ID,
  getWorkPermitTechnicianDepartmentId,
  TASKS_DETAILS_DIALOG_ROUTE,
  TECHNICIAN_TASKLIST_ROUTE
} from '../../../../utils/constants';
import { formatDateWithTime } from '../../../../utils/dateHandling';
import { getEnumDisplayValue } from '../../../../utils/enumUtils';
import { log } from '../../../../utils/logging/log';
import { getWorkPermitMainTaskId, isWorkPermitWaitingForTechnician } from '../../../../utils/workPermitTaskSeed';
import { getWorkPermitTimes, hasWorkPermitTimeMismatch } from '../../../../utils/workPermitTimes';
import { RowLabel, RowValue } from '../components/TextRow';
import WorkPermitMessageDialog from './WorkPermitMessageDialog';
import WorkPermitTimeDialog from './WorkPermitTimeDialog';

interface Props {
  workPermit: WorkTaskDTO2;
  currentTask?: WorkTaskDTO2;
  isCurrentTask: boolean;
  userDepartmentId?: number;
  onOpen: (workPermitTaskId: number) => void;
  onChanged: () => void;
}

type MessageDialog = 'sendToPlanning' | 'sendBackToTechnician' | 'sendToControlRoom';

const isFinished = (status?: WorkTaskStatus) =>
  status === WorkTaskStatus.Completed || status === WorkTaskStatus.Processed || status === WorkTaskStatus.Canceled;

// What happens next with the work permit, based on its status and the department it is waiting in
const getNextStepText = (workPermit: WorkTaskDTO2): string | undefined => {
  if (workPermit.status === WorkTaskStatus.Canceled) return 'Arbejdstilladelsen er annulleret.';
  if (isFinished(workPermit.status)) return 'Arbejdstilladelsen er godkendt.';

  const departmentId = workPermit.assignedToDepartment?.departmentId;
  if (isWorkPermitWaitingForTechnician(workPermit)) {
    return 'Arbejdstilladelsen venter på rettelser fra tekniker, se eventuelt beskeder. Når rettelserne er udført, skal arbejdstilladelsen sendes tilbage til Kontrolrummet.';
  }
  if (departmentId === CONTROL_ROOM_DEPARTMENT_ID && workPermit.status === WorkTaskStatus.Created) {
    return 'Arbejdstilladelsen afventer godkendelse i Kontrolrummet. Hvis tiden ikke passer, sendes den til Planlægning. Mangler der information i arbejdstilladelsen, sendes den tilbage til teknikeren, som har oprettet den.';
  }
  if (departmentId === PLANNING_DEPARTMENT_ID) {
    return 'Arbejdstilladelsen afventer Planlægning, se eventuelt beskeder. Ret først tiden på hovedopgaven, derefter tiden på arbejdstilladelsen med "Ret tid", og send den så til Kontrolrummet.';
  }
};

const draftMissingInfoMail = (workPermit: WorkTaskDTO2, message: string) => {
  const subject = `Arbejdstilladelse ${workPermit.id} mangler information`;
  const taskLink =
    `${window.location.origin}/${TECHNICIAN_TASKLIST_ROUTE}${TASKS_DETAILS_DIALOG_ROUTE}` +
    `?id=${encodeURIComponent(workPermit.id ?? '')}&type=${BaseWorkTaskTypeEnum.WorkTask}`;
  // mailto bodies are plain text, so the link is left bare on a line of its own with CRLF line breaks,
  // which is what mail clients need to turn it into a clickable link
  const body = [
    message,
    '',
    'Du kan finde opgaven i din opgaveliste under fanen "Arbejdstilladelser (KOS)" eller ved at følge dette link:',
    taskLink,
    '',
    'Du kan rette i opgaven ved at klikke på blyanten ved siden af titlen.'
  ].join('\r\n');
  window.location.href = `mailto:${workPermit.createdByEmail ?? ''}?subject=${encodeURIComponent(
    subject
  )}&body=${encodeURIComponent(body)}`;
};

const WorkPermitCard = (props: Props) => {
  const { workPermit, currentTask, isCurrentTask, userDepartmentId, onOpen, onChanged } = props;
  const [messageDialog, setMessageDialog] = useState<MessageDialog>();
  const [isApproving, setIsApproving] = useState(false);
  const [isSendingToControlRoom, setIsSendingToControlRoom] = useState(false);
  const [isTimeDialogOpen, setIsTimeDialogOpen] = useState(false);
  const [mainTask, setMainTask] = useState<WorkTaskDTO2>();

  const workPermitTaskId = parseInt(workPermit.id ?? '');
  const workPermitDepartmentId = workPermit.assignedToDepartment?.departmentId;

  // Each department acts on the work permits in its own queue: the control room while a work permit is
  // created, planning until it is finished.
  const showControlRoomActions =
    userDepartmentId === CONTROL_ROOM_DEPARTMENT_ID &&
    workPermitDepartmentId === CONTROL_ROOM_DEPARTMENT_ID &&
    workPermit.status === WorkTaskStatus.Created;
  const showPlanningActions =
    userDepartmentId === PLANNING_DEPARTMENT_ID &&
    workPermitDepartmentId === PLANNING_DEPARTMENT_ID &&
    !isFinished(workPermit.status);

  // The bookings are on the main task. It is not fetched when the task being viewed is the main task, as
  // it then already includes the bookings.
  const mainTaskId = getWorkPermitMainTaskId(workPermit);
  useEffect(() => {
    if (!mainTaskId || mainTaskId === workPermitTaskId) return;
    if (currentTask?.id && parseInt(currentTask.id) === mainTaskId) {
      setMainTask(currentTask);
      return;
    }

    let isCurrent = true;
    TaskService.getTask(mainTaskId)
      .then((task) => isCurrent && setMainTask(task))
      .catch((error) => log(error));
    return () => {
      isCurrent = false;
    };
  }, [mainTaskId, workPermitTaskId, currentTask]);

  const nextStepText = getNextStepText(workPermit);
  const showTimeMismatchWarning = !!mainTask && hasWorkPermitTimeMismatch(workPermit, mainTask);

  const handleSaveTime = useCallback(
    async (start: Date, end: Date) => {
      await TaskService.updateWorkTask(workPermitTaskId, {
        earliestStartDate: start.toISOString(),
        plannedArrival: start.toISOString(),
        deadline: end.toISOString()
      });
      NotificationService.success(`Tiden på arbejdstilladelse ${workPermitTaskId} er rettet`);
      onChanged();
    },
    [onChanged, workPermitTaskId]
  );

  const postMessage = useCallback(
    async (message: string) => {
      await TaskService.postComment({ baseWorkTaskId: workPermitTaskId, text: message });
    },
    [workPermitTaskId]
  );

  const sendToDepartment = useCallback(
    async (message: string, departmentId: number, departmentName: string) => {
      await postMessage(message);
      await TaskService.updateDepartments([{ workTaskId: workPermitTaskId, newDepartmentId: departmentId }]);
      NotificationService.success(`Arbejdstilladelse ${workPermitTaskId} er sendt til ${departmentName}`);
      onChanged();
    },
    [onChanged, postMessage, workPermitTaskId]
  );

  const handleSendToControlRoom = useCallback(() => {
    setIsSendingToControlRoom(true);
    TaskService.updateDepartments([{ workTaskId: workPermitTaskId, newDepartmentId: CONTROL_ROOM_DEPARTMENT_ID }])
      .then(() => {
        NotificationService.success(`Arbejdstilladelse ${workPermitTaskId} er sendt til Kontrolrummet`);
        onChanged();
      })
      .catch((error) => {
        log(error);
        NotificationService.error('Kunne ikke sende arbejdstilladelsen til Kontrolrummet');
      })
      .finally(() => setIsSendingToControlRoom(false));
  }, [onChanged, workPermitTaskId]);

  // Sending back to the technician moves the work permit out of the queue of Kontrolrummet
  const sendBackToTechnician = useCallback(
    async (message: string) => {
      await postMessage(message);
      await TaskService.updateDepartments([
        { workTaskId: workPermitTaskId, newDepartmentId: getWorkPermitTechnicianDepartmentId() }
      ]);
      NotificationService.success('Beskeden er gemt, og arbejdstilladelsen er sendt tilbage til teknikeren');
      onChanged();
    },
    [onChanged, postMessage, workPermitTaskId]
  );

  const handleApprove = useCallback(() => {
    setIsApproving(true);
    TaskService.setTaskStatus([
      { workTaskId: workPermitTaskId, status: WorkTaskStatus.Completed, workTaskHasSubtasks: !!workPermit.hasSubTasks }
    ])
      .then(() => {
        NotificationService.success(`Arbejdstilladelse ${workPermitTaskId} er godkendt`);
        onChanged();
      })
      .catch((error) => {
        log(error);
        NotificationService.error('Kunne ikke godkende arbejdstilladelsen');
      })
      .finally(() => setIsApproving(false));
  }, [onChanged, workPermit.hasSubTasks, workPermitTaskId]);

  const handleSendBackAndMail = useCallback(
    async (message: string) => {
      await sendBackToTechnician(message);
      draftMissingInfoMail(workPermit, message);
    },
    [sendBackToTechnician, workPermit]
  );

  return (
    <Card>
      <CardHeader>
        <Link onClick={() => !isCurrentTask && onOpen(workPermitTaskId)} disabled={isCurrentTask}>
          <Typography variant="h5" fontWeight="bold" color={isCurrentTask ? 'greyedOut' : 'inherit'}>
            {`${workPermit.taskType?.name ?? 'Arbejdstilladelse'} ${workPermit.id}`}
          </Typography>
        </Link>
        {isCurrentTask && <Typography fontStyle="oblique">Denne opgave</Typography>}
        <StatusContainer>
          <StatusTag workTaskStatus={workPermit.status}>
            {workPermit.status && getEnumDisplayValue(WorkTaskStatus[workPermit.status])}
          </StatusTag>
        </StatusContainer>
      </CardHeader>

      <Fields>
        <Field label="Asset Id">{workPermit.assetId || '-'}</Field>
        <Field label="Afdeling">{workPermit.assignedToDepartment?.name || '-'}</Field>
        <Field label="Startdato">
          {workPermit.earliestStartDate ? formatDateWithTime(workPermit.earliestStartDate) : '-'}
        </Field>
        <Field label="Slutdato">{workPermit.deadline ? formatDateWithTime(workPermit.deadline) : '-'}</Field>
        <Field label="Oprettet af (Arbejdsleder)">{workPermit.createdByName || '-'}</Field>
        <Field label="Tekniker (Holdleder)">{workPermit.assignedToName || '-'}</Field>
      </Fields>

      {(nextStepText || showTimeMismatchWarning) && (
        <NextStep>
          {nextStepText && (
            <>
              <Typography fontWeight="bold">Næste skridt</Typography>
              <Typography>{nextStepText}</Typography>
            </>
          )}
          {showTimeMismatchWarning && (
            <Warning>
              <WarningIcon color={theme.palette.functions.error.primary} size="20px" />
              <Typography>Tiden på arbejdstilladelsen passer ikke med tiden på hovedopgaven.</Typography>
            </Warning>
          )}
        </NextStep>
      )}

      {showControlRoomActions && (
        <Actions>
          <Button compact onClick={handleApprove} isLoading={isApproving} disabled={isApproving}>
            Godkend
          </Button>
          <Button compact variant="secondary" onClick={() => setMessageDialog('sendToPlanning')} disabled={isApproving}>
            Send til planlægning med en besked
          </Button>
          <Button
            compact
            variant="secondary"
            onClick={() => setMessageDialog('sendBackToTechnician')}
            disabled={isApproving}
          >
            Send tilbage til tekniker med besked
          </Button>
        </Actions>
      )}
      {isWorkPermitWaitingForTechnician(workPermit) && (
        <Actions>
          <Button
            compact
            onClick={handleSendToControlRoom}
            isLoading={isSendingToControlRoom}
            disabled={isSendingToControlRoom}
          >
            Send til kontrolrum
          </Button>
        </Actions>
      )}
      {showPlanningActions && (
        <Actions>
          {mainTask && (
            <Button compact variant="secondary" onClick={() => setIsTimeDialogOpen(true)}>
              Ret tid
            </Button>
          )}
          <Button compact variant="secondary" onClick={() => setMessageDialog('sendToControlRoom')}>
            Send til kontrolrummet med besked
          </Button>
        </Actions>
      )}

      <WorkPermitMessageDialog
        open={messageDialog === 'sendToPlanning'}
        title="Send til planlægning"
        description={
          'Arbejdstilladelsen sendes til Planlægning, så der kan findes et nyt tidspunkt. Beskeden gemmes i menupunktet "Beskeder" på arbejdstilladelsen.'
        }
        messageRequired
        actions={[
          {
            text: 'Send til planlægning',
            errorText: 'Kunne ikke sende arbejdstilladelsen til Planlægning',
            onSubmit: (message) => sendToDepartment(message, PLANNING_DEPARTMENT_ID, 'Planlægning')
          }
        ]}
        handleClose={() => setMessageDialog(undefined)}
      />
      <WorkPermitTimeDialog
        open={isTimeDialogOpen}
        defaultTimes={mainTask && getWorkPermitTimes(mainTask)}
        onSubmit={handleSaveTime}
        handleClose={() => setIsTimeDialogOpen(false)}
      />
      <WorkPermitMessageDialog
        open={messageDialog === 'sendToControlRoom'}
        title="Send til kontrolrummet"
        description={
          'Når du har fundet en ny tid til hovedopgaven, kan du sende arbejdstilladelsen tilbage til Kontrolrummet. Beskeden gemmes i menupunktet "Beskeder" på arbejdstilladelsen.'
        }
        messageRequired
        actions={[
          {
            text: 'Send til kontrolrummet',
            errorText: 'Kunne ikke sende arbejdstilladelsen til Kontrolrummet',
            onSubmit: (message) => sendToDepartment(message, CONTROL_ROOM_DEPARTMENT_ID, 'Kontrolrummet')
          }
        ]}
        handleClose={() => setMessageDialog(undefined)}
      />
      <WorkPermitMessageDialog
        open={messageDialog === 'sendBackToTechnician'}
        title="Send tilbage til tekniker"
        description={
          'Beskriv hvad teknikeren skal rette eller udfylde. Arbejdstilladelsen bliver hos teknikeren, indtil teknikeren sender den tilbage til Kontrolrummet. Beskeden gemmes i menupunktet "Beskeder" på arbejdstilladelsen' +
          (workPermit.createdByEmail ? `, og du kan også vælge at sende en mail til ${workPermit.createdByEmail}.` : '.')
        }
        messageRequired
        actions={[
          { text: 'Opret besked', errorText: 'Kunne ikke gemme beskeden', onSubmit: sendBackToTechnician },
          {
            text: 'Opret besked og send mail',
            errorText: 'Kunne ikke gemme beskeden',
            onSubmit: handleSendBackAndMail
          }
        ]}
        handleClose={() => setMessageDialog(undefined)}
      />
    </Card>
  );
};

const Field = (props: { label: string; children: React.ReactNode }) => (
  <FieldCell>
    <RowLabel>{props.label}</RowLabel>
    <FieldValue>{props.children}</FieldValue>
  </FieldCell>
);

const Card = styled.div`
  display: flex;
  flex-direction: column;
  row-gap: 8px;
  padding: 16px;
  border: 1px solid ${(props) => props.theme.palette.grey.black10};
  border-radius: 8px;
`;

const CardHeader = styled.div`
  display: flex;
  align-items: center;
  column-gap: 12px;
  margin-bottom: 8px;
`;

const StatusContainer = styled.div`
  margin-left: auto;
`;

const Fields = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px 16px;
`;

const FieldCell = styled.div`
  display: flex;
  flex-direction: column;
  row-gap: 2px;
`;

const FieldValue = styled(RowValue)`
  flex: none;
`;

const NextStep = styled.div`
  display: flex;
  flex-direction: column;
  row-gap: 8px;
  margin-top: 8px;
  padding-top: 16px;
  border-top: 1px solid ${(props) => props.theme.palette.grey.black10};
`;

const Warning = styled.div`
  display: flex;
  align-items: center;
  column-gap: 8px;
`;

const Actions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-top: 8px;
`;

export default WorkPermitCard;
